<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { Question } from '../../global'
import InformationButton from '../InformationButton.vue'
import CheckboxGroup from '../Input/CheckboxGroup.vue'
import Input from '../Input/Input.vue'
import InputGroup from '../InputGroup.vue'
import { Label } from '../Label'

const props = defineProps<{
    question: Question
    errorMessage: string | undefined
}>()

const emit = defineEmits<{
    select: [answerKey: string]
    toggle: [answerKey: string, selected: boolean]
    fill: [filled: boolean]
}>()

const { t } = useI18n()

const placeholder = computed<string>(() => (props.question.required ? '' : t('Optional')))

const model = defineModel<any>()

function toggleCheckbox(answerId: string, checked: boolean) {
    const option = props.question.selectOptions?.find(
        (selectOption) => selectOption.answerId === answerId
    )

    if (option) {
        emit('toggle', option.key, checked)
    }
}

function emitFill() {
    const value = model.value
    emit('fill', value !== undefined && value !== null && String(value).trim() !== '')
}
</script>

<template>
    <div>
        <Label class="gap-x-2">
            <span>
                {{ question.prompt }}
                <span
                    v-if="!question.required && question.answerType === 'select'"
                    class="inline-block translate-y-[-1px] text-xs font-normal text-neutral-500"
                >
                    {{ $t('Optional') }}
                </span>
            </span>
            <div v-if="question.hint" class="ml-auto">
                <InformationButton
                    :title="question.hint.title"
                    :description="question.hint.description"
                />
            </div>
        </Label>
        <p v-if="errorMessage" class="mt-1 text-xs text-danger-800">
            {{ errorMessage }}
        </p>
        <fieldset class="mt-4">
            <legend class="sr-only">{{ question.prompt }}</legend>
            <div v-if="question.answerType === 'select'" class="space-y-4">
                <CheckboxGroup
                    v-if="question.allowMultipleSelect"
                    v-model="model"
                    :options="
                        (question.selectOptions || []).map((selectOption) => ({
                            label: selectOption.text,
                            value: selectOption.answerId,
                        }))
                    "
                    @toggle="toggleCheckbox"
                />
                <div
                    v-else
                    v-for="selectOption in question.selectOptions"
                    :key="selectOption.answerId"
                    class="flex items-center space-x-2"
                >
                    <input
                        type="radio"
                        v-model="model"
                        :id="selectOption.answerId"
                        :name="question.questionId"
                        :value="selectOption.answerId"
                        class="size-4 border-gray-300 text-primary-800 focus:ring-blue-800"
                        @change="emit('select', selectOption.key)"
                    />
                    <Label :for="selectOption.answerId">
                        {{ selectOption.text }}
                    </Label>
                </div>
            </div>
            <div v-if="question.answerType === 'numeric'">
                <InputGroup :id="question.questionId" :suffix="question.displayUnitOfMeasurement">
                    <Input
                        type="number"
                        inputmode="numeric"
                        :min="question.configuration?.rules?.min"
                        :max="question.configuration?.rules?.max"
                        :id="question.questionId"
                        v-model="model"
                        :placeholder="placeholder"
                        @blur="emitFill"
                    />
                </InputGroup>
            </div>
            <div v-if="question.answerType === 'simple'">
                <Input
                    type="text"
                    :id="question.questionId"
                    v-model.trim="model"
                    :placeholder="placeholder"
                    @blur="emitFill"
                />
            </div>
        </fieldset>
    </div>
</template>
